<template>
  <div class="modules" >
    <s-table
      class="modules__table no-align"
      :fields="fields"
      :items="modules"
      :busy="loading"
    >
      <template #head(check)>
        <div class="modules__check-wrapper">
          <b-form-checkbox
            :checked="allSelected"
            @click.native.stop
            @change="toggleAll"
          />
        </div>
      </template>
      <template #cell(check)="data">
        <b-form-checkbox v-model="data.item.selected" />
      </template>

      <template #cell(associatedAssets)="data">
        <list-aggregator
          suppressed-label-path="profile-manager.editing.modules.suppressed-assets"
          :modal-table-items="data.item.associatedAssets"
          :modal-title="data.item.name"
          :modal-table-header="$t('profile-manager.editing.modules.asset')"
        />
      </template>
    </s-table>
  </div>
</template>

<script>
import { uniqBy } from 'lodash';
import ListAggregator from '@/components/listAggregator';
import ProfileEditService from '@/domain/services/generalPanel/adminPanel/profile/profileEditService';
import getModuleDisplayName from '@/services/moduleDisplayName';

export default {
  components: {
    ListAggregator,
  },
  props: {
    profile: {
      type: Object,
      default: () => ({}),
    },
  },
  data: () => ({
    loading: true,
    modules: [],
  }),
  methods: {
    toggleAll(checked) {
      this.modules.forEach((it) => {
        it.selected = checked;
      });
    },
  },
  computed: {
    assetsIds() {
      return this.profile.assets.map(it => it.id);
    },
    fields() {
      return [
        { key: 'check', class: 'modules__cell-check' },
        {
          key: 'name',
          label: this.$t('profile-manager.editing.modules.name'),
          class: 'modules__cell-module-name',
        },
        {
          key: 'menu',
          label: this.$t('profile-manager.editing.modules.menu'),
          class: 'modules__cell-module-menu',
        },
        {
          key: 'associatedAssets',
          label: this.$t('profile-manager.editing.modules.associated-assets'),
          class: 'modules__cell-associated-assets',
        },
      ];
    },
    allSelected() {
      return this.modules.length > 0 && this.modules.every(it => it.selected);
    },
    selection() {
      return this.modules.filter(it => it.selected);
    },
  },
  async created() {
    this.modules = await ProfileEditService.getProfileModules({
      profileId: this.profile.id,
      parentProfileId: this.profile.parentProfileId,
      assetsIds: this.assetsIds,
    });
    this.modules = this.modules.map(module => ({
      ...module,
      name: getModuleDisplayName(module.name),
    }));
    this.loading = false;
    this.$emit('setInitialValues:modules', this.modules.filter(module => module.selected));

    this.profile.modules.forEach(({ id: moduleId }) => {
      const module = this.modules.find(it => it.id === moduleId);
      if (module) {
        module.selected = true;
      }
    });
  },
  watch: {
    selection(items) {
      if (!this.loading) {
        const preservedItems = this.profile.modules.filter(oldItem => !this.modules
          .some(currentItem => currentItem.id === oldItem.id));
        this.profile.modules = uniqBy([...preservedItems, ...items], 'id');
      }
    },
  },
};
</script>

<style lang="sass" scoped>
@import "src/styles/defaults/variables"
@import "src/styles/defaults/mobile-first"

.modules
  &__table ::v-deep
    max-height: 430px
  &__check-wrapper
    display: flex
  &__loader
    font-size: 2em
    margin-bottom: 1em
  ::v-deep &__cell
    &-check
      width: 40px
    &-module-name,
    &-module-menu,
    &-associated-assets
      text-align: center
  &__associated
    display: flex
    align-items: center
    justify-content: center
  ::v-deep &__assets-summary-label_highlight
    text-decoration: underline
    color: var(--color-info)
  &__show-more-assets-link:hover
    text-decoration: none
  +media-min-sm()
    &__table ::v-deep
      max-height: initial
      tbody
        display: block
        max-height: 430px
        overflow: auto
      tbody, thead
        tr
          display: table
          width: 100%
          table-layout: fixed
  +media-min-md()
  +media-min-lg()
</style>
